// frontend/src/components/CompartilharAcessoCard/CompartilharAcessoCard.jsx
import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Alert, List, Tag, Popconfirm, App, Space } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  convidarCompartilhamento,
  revogarCompartilhamento,
  sairDoWorkspace,
} from '../../services/compartilhamentoService';
import { getErrorMessage } from '../../utils/errorHandler';

const { Text, Paragraph } = Typography;

const STATUS_LABEL = {
  pendente: <Tag color="orange">Aguardando aceite</Tag>,
  ativo: <Tag color="green">Ativo</Tag>,
};

export default function CompartilharAcessoCard({ isMobile }) {
  const { message } = App.useApp();
  const { concedido, recebidos, recarregarCompartilhamento, trocarWorkspace } = useWorkspace();
  const [form] = Form.useForm();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const convidar = async ({ email }) => {
    setEnviando(true);
    setErro('');
    try {
      await convidarCompartilhamento(email.trim());
      form.resetFields();
      message.success('Convite enviado.');
      await recarregarCompartilhamento();
    } catch (e) {
      setErro(getErrorMessage(e, 'Não foi possível enviar o convite.'));
    } finally {
      setEnviando(false);
    }
  };

  const revogar = async (id) => {
    try {
      await revogarCompartilhamento(id);
      message.success('Acesso encerrado.');
      await recarregarCompartilhamento();
    } catch (e) {
      message.error(getErrorMessage(e, 'Não foi possível encerrar o acesso.'));
    }
  };

  const sair = async (id) => {
    try {
      await sairDoWorkspace(id);
      message.success('Você saiu da conta compartilhada.');
      trocarWorkspace(null);
    } catch (e) {
      message.error(getErrorMessage(e, 'Não foi possível sair.'));
    }
  };

  return (
    <Card
      title={<span><TeamOutlined /> Compartilhar acesso</span>}
      bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
      style={{ marginTop: 24 }}
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Convide uma pessoa (cônjuge ou família) para acompanhar e organizar junto as suas
        finanças e milhas. Ela entra com o próprio login e cada lançamento fica marcado com
        quem fez. Adiciona <strong>R$ 5,00/mês</strong> à sua assinatura, a partir da próxima
        cobrança.
      </Paragraph>

      {concedido ? (
        <List
          size="small"
          bordered
          dataSource={[concedido]}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="revogar"
                  title="Encerrar o acesso desta pessoa?"
                  okText="Encerrar"
                  cancelText="Cancelar"
                  onConfirm={() => revogar(a.id)}
                >
                  <Button danger size="small">Encerrar</Button>
                </Popconfirm>,
              ]}
            >
              <Space direction="vertical" size={0}>
                <Text strong>{a.convidado?.nome || a.convite_email}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {a.convidado?.email || a.convite_email} {STATUS_LABEL[a.status]}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      ) : (
        <Form form={form} layout={isMobile ? 'vertical' : 'inline'} onFinish={convidar}>
          <Form.Item
            name="email"
            rules={[{ required: true, type: 'email', message: 'Informe um e-mail válido' }]}
            style={{ flex: 1, minWidth: 220 }}
          >
            <Input placeholder="e-mail de quem você quer convidar" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={enviando}>Convidar</Button>
          </Form.Item>
        </Form>
      )}

      {erro && <Alert type="error" showIcon message={erro} style={{ marginTop: 12 }} />}

      {recebidos && recebidos.length > 0 && (
        <>
          <div style={{ margin: '20px 0 8px', fontWeight: 600 }}>Contas que compartilharam com você</div>
          <List
            size="small"
            bordered
            dataSource={recebidos}
            renderItem={(a) => (
              <List.Item
                actions={[
                  <Button key="abrir" size="small" onClick={() => trocarWorkspace(a.titular?.id)}>
                    Abrir
                  </Button>,
                  <Popconfirm
                    key="sair"
                    title="Sair desta conta compartilhada?"
                    okText="Sair"
                    cancelText="Cancelar"
                    onConfirm={() => sair(a.id)}
                  >
                    <Button danger size="small" type="text">Sair</Button>
                  </Popconfirm>,
                ]}
              >
                <Text>Conta de <strong>{a.titular?.nome}</strong></Text>
              </List.Item>
            )}
          />
        </>
      )}
    </Card>
  );
}
